// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { CardVariant, IWebsiteProduct } from '@src/components/product-card/product-card.types'
import { getFormattedSlug } from '@src/pages/[...slug]/components/collection/utils'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getColorSwatchPosition,
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { PAGE_VARIANT } from '@src/quince-site-common/src/domains/PageTypes'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import { BaseProductVariant } from '@src/quince-site-common/src/domains/ProductVariant'
import { RECOMMENDATION_TRACKING_KEYS } from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/molecules/cartPopupTwo/molecules/recommendedForYou/molecules/product-list-carousel/product-list-carousel.constants'
import { getPageContextDetails } from '@src/quince-site-common/src/utils/getPageContextDetails'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { isProductImage } from '@src/quince-site-common/src/utils/typeGuards'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

interface TabData {
  rec_module_id?: string
  rec_element_name?: string
  rec_product_count?: string
  is_cart_modal?: boolean
}

const getTabDataFromContext = (context?: Record<string, unknown>): TabData | undefined => {
  if (!context?.tabData || typeof context.tabData !== 'object') {
    return undefined
  }

  const tabData = context.tabData as Record<string, unknown>

  return {
    rec_module_id: tabData[RECOMMENDATION_TRACKING_KEYS.REC_MODULE_ID] as string | undefined,
    rec_element_name: tabData[RECOMMENDATION_TRACKING_KEYS.REC_ELEMENT_NAME] as string | undefined,
    rec_product_count: tabData[RECOMMENDATION_TRACKING_KEYS.REC_PRODUCT_COUNT] as string | undefined,
    is_cart_modal: tabData[RECOMMENDATION_TRACKING_KEYS.IS_CART_MODAL] as boolean | undefined,
  }
}

export interface ProductProperties {
  product_id: string
  title: string
  color?: string
  count_color_variant: number
  rating: string
  tags: string[]
  tag_ids: string[]
  review_count: number
  oos_flag: number
  division: string
  division_id: string
  department: string
  department_id: string
  subdepartment: string
  subdepartment_id: string
  rec_module_id?: string
  rec_element_name?: string
  rec_product_count?: string
  is_cart_modal?: boolean
  product_gallery_photo_id?: string
  has_product_fit_details?: boolean
  product_fit_details_text?: string
  product_gallery_photo_position?: number
  is_minipdp?: boolean
  price?: string | number
  quantity?: number
  discounted_price?: number | string
  is_preorder?: boolean
  preorder_edd?: string
  color_variant?: string[]
  color_swatch_position?: number
}

const CORE_COLOR_GROUP = 'Core Color'
const COLOR_OPTION_NAME = 'color'

const getColorOptionValuesBySwatchSection = (product: Product | Bundle): string[][] | undefined => {
  const productColorOption = product?.productOptions?.find(po => po.name.toLowerCase() === COLOR_OPTION_NAME)
  const orderedColorValues = productColorOption?.values?.map(value => value.value) ?? []
  const variants = (product as Product).variants
  if (!orderedColorValues.length || !variants?.length) return undefined

  const groupedColorValuesBySection = new Map<string, Set<string>>()
  variants.forEach((variant: BaseProductVariant) => {
    const colorGrouping = variant?.colorGrouping
    if (!colorGrouping || colorGrouping === CORE_COLOR_GROUP) return

    const selectedColorValue = variant.options.find(option => option.name.toLowerCase() === COLOR_OPTION_NAME)?.value
    if (!selectedColorValue) return

    if (!groupedColorValuesBySection.has(colorGrouping)) {
      groupedColorValuesBySection.set(colorGrouping, new Set<string>())
    }
    groupedColorValuesBySection.get(colorGrouping)?.add(selectedColorValue)
  })

  if (!groupedColorValuesBySection.size) return undefined

  const groupedColorValuesInDisplayOrder = Array.from(groupedColorValuesBySection.values())
    .map(groupedColors => orderedColorValues.filter(color => groupedColors.has(color)))
    .filter(colors => colors.length > 0)

  const groupedColorValueSet = new Set(groupedColorValuesInDisplayOrder.flat())
  const coreColorValues = orderedColorValues.filter(color => !groupedColorValueSet.has(color))

  return [...(coreColorValues.length > 0 ? [coreColorValues] : []), ...groupedColorValuesInDisplayOrder]
}

const getColorSwatchPositionFromSections = (
  selectedColorValue: string | undefined,
  groupedColorValues: string[][],
): number | undefined => {
  if (!selectedColorValue) return undefined

  for (const colorsInSection of groupedColorValues) {
    const colorPosition = getColorSwatchPosition(selectedColorValue, colorsInSection)
    if (colorPosition !== undefined) {
      return colorPosition
    }
  }

  return undefined
}

export function getProductViewColorAnalyticsFields(
  product: Product | Bundle,
  selectedColorValue: string | undefined,
): { color_variant?: string[]; color_swatch_position?: number } {
  const productColorOption = product?.productOptions?.find(po => po.name.toLowerCase() === 'color') ?? undefined
  const colorOrderFromNewOptions = (product as Product).new_color_options?.map(o => o.value)
  const color_variant: string[] | undefined =
    colorOrderFromNewOptions && colorOrderFromNewOptions.length > 0
      ? colorOrderFromNewOptions
      : productColorOption?.values?.map(v => v.value)
  const list = color_variant && color_variant.length > 0 ? color_variant : undefined
  const groupedColorValues = getColorOptionValuesBySwatchSection(product)
  const color_swatch_position =
    (groupedColorValues ? getColorSwatchPositionFromSections(selectedColorValue, groupedColorValues) : undefined) ??
    getColorSwatchPosition(selectedColorValue, list)
  return {
    ...(list ? { color_variant: list } : {}),
    ...(color_swatch_position !== undefined ? { color_swatch_position } : {}),
  }
}

// This function is used when we need to check if the current PDP popup is miniPDP popup.
// Used primarily for telemetry tracking of variant selected and add to cart events.
export const isMiniPDP = (productSlug: string): boolean => {
  if (!isBrowser()) return false

  const pageContextDetails = getPageContextDetails()
  const formattedProductSlug = getFormattedSlug(productSlug)
  const formattedPathname = getFormattedSlug(window.location.pathname)
  return (
    pageContextDetails.currentPage?.page_variant === PAGE_VARIANT.MINI_PDP && formattedProductSlug === formattedPathname
  )
}

export const getProductPropertiesForTracking = (
  product: Product | Bundle,
  variantId?: string,
  propertyOverrides?: Partial<ProductProperties>,
): ProductProperties => {
  let variant = undefined

  if (variantId) {
    variant = (product as Product)?.variants?.find(vr => vr.svid?.toString() === variantId.toString())
  }

  const productColorOption = product?.productOptions?.find(po => po.name.toLowerCase() === 'color') ?? undefined
  const firstProductColorOptionValue = productColorOption?.values?.[0]
  const selectedColorOptionValue =
    variantId && variant
      ? (variant.options.find(opt => opt.name.toLowerCase() === 'color') ?? undefined)
      : firstProductColorOptionValue
  const division = product.classification?.division
  const department = product.classification?.department
  const subDepartment = product.classification?.subDepartment
  const productTagsForSelectedColor: string[] = []
  product.tags?.forEach(tg => {
    const isTagForColor = tg.options?.some(
      tgOpt => tgOpt.value.toLowerCase() === selectedColorOptionValue?.value.toLowerCase(),
    )
    if (isTagForColor) {
      productTagsForSelectedColor.push(tg.value)
    }
  })

  return {
    product_id: product?.spid?.toString(),
    title: product.title,
    color: selectedColorOptionValue?.value,
    count_color_variant: productColorOption?.values?.length ?? 0,
    rating: product.productReviewSummary?.averageRating?.toString() ?? '',
    tags: productTagsForSelectedColor,
    tag_ids: transformTagsIntoAnalyticsTags(productTagsForSelectedColor),
    review_count: product.productReviewSummary?.reviewCount ?? 0,
    oos_flag: getIndividualTagStatus(productTagsForSelectedColor as ProductTagKey[]).sold_out,
    division: division?.name ?? '',
    division_id: division?.id?.toString() ?? '',
    department: department?.name ?? '',
    department_id: department?.id?.toString() ?? '',
    subdepartment: subDepartment?.name ?? '',
    subdepartment_id: subDepartment?.id?.toString() ?? '',
    price: variant?.price,
    ...propertyOverrides,
  }
}

export const getProductPropertiesForTrackingForCard = (
  product: IWebsiteProduct,
  tags: string[],
  selectedVariant?: CardVariant,
  productClickTrackConfig?: TrackConfig,
  propertyOverrides?: Partial<ProductProperties>,
): ProductProperties & { product_position?: number } => {
  const tabData = getTabDataFromContext(productClickTrackConfig?.context)

  return {
    product_id: product.productId.toString(),
    title: product.title,
    color: selectedVariant?.displayConfig.displayName,
    count_color_variant: product.colorVariants?.length ?? 0,
    rating: product.reviews.averageRating.toString(),
    tags,
    tag_ids: transformTagsIntoAnalyticsTags(tags),
    review_count: product.reviews.reviewCount,
    oos_flag: selectedVariant?.tags?.some(({ value }) => value === 'Sold Out') ? 1 : 0,
    division: product.classification?.division ?? '',
    division_id: product.classification?.division_id?.toString() ?? '',
    department: product.classification?.department ?? '',
    department_id: product.classification?.department_id?.toString() ?? '',
    subdepartment: product.classification?.subdepartment ?? '',
    subdepartment_id: product.classification?.subdepartment_id?.toString() ?? '',
    rec_module_id: tabData?.rec_module_id?.toString() ?? '',
    rec_element_name: tabData?.rec_element_name?.toString() ?? '',
    rec_product_count: tabData?.rec_product_count?.toString() ?? '',
    is_cart_modal: Boolean(tabData?.is_cart_modal),
    price: selectedVariant?.price?.salePrice,
    product_position: productClickTrackConfig?.context?.product_position as number | undefined,
    ...propertyOverrides,
  }
}

export const getProductPropertiesForTrackingNew = ({
  product,
  productVariant,
  tags,
  propertyOverrides,
}: {
  product: IWebsiteProduct
  productVariant?: CardVariant
  tags: string[]
  propertyOverrides?: Partial<ProductProperties>
}): ProductProperties => {
  return {
    product_id: product?.relatedIds.spid?.toString(),
    title: product.title,
    color: productVariant?.displayConfig?.value,
    count_color_variant: product?.colorVariants?.length ?? 0,
    rating: product?.reviews?.averageRating?.toString() ?? '',
    tags: tags,
    tag_ids: transformTagsIntoAnalyticsTags(tags),
    review_count: product?.reviews?.reviewCount ?? 0,
    oos_flag: getIndividualTagStatus(tags as ProductTagKey[]).sold_out,
    division: product?.classification?.division ?? '',
    division_id: product?.classification?.division_id?.toString() ?? '',
    department: product?.classification?.department ?? '',
    department_id: product?.classification?.department_id?.toString() ?? '',
    subdepartment: product?.classification?.subdepartment ?? '',
    subdepartment_id: product?.classification?.subdepartment_id?.toString() ?? '',
    price: productVariant?.price?.salePrice,
    ...propertyOverrides,
  }
}

export const getProductMediaPropertiesForTracking = (media: ProductMedia[], selectedMedia: ProductMedia) => {
  const isImageMedia = selectedMedia && isProductImage(selectedMedia)
  const imagePosition = media.findIndex(
    m => isProductImage(m) && isProductImage(selectedMedia) && m.image.id === selectedMedia.image.id,
  )

  return {
    product_gallery_photo_id: isImageMedia ? selectedMedia.image.id : '',
    has_product_fit_details: Boolean(isImageMedia && selectedMedia.image.displayModelInfo),
    product_fit_details_text: isImageMedia ? selectedMedia.image.description : '',
    product_gallery_photo_position: imagePosition > -1 ? imagePosition + 1 : undefined,
  }
}
